import { label, useEnvironment } from "@keycloak/keycloak-ui-shared";
import {
  Button,
  Label,
  Nav,
  NavGroup,
  PageSidebar,
  PageSidebarBody,
  Tooltip,
} from "@patternfly/react-core";
import { FormEvent, ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { useAccess } from "./context/access/Access";
import { useRealm } from "./context/realm-context/RealmContext";
import { useServerInfo } from "./context/server-info/ServerInfoProvider";
import { Environment } from "./environment";
import { toPage } from "./page/routes";
import { routes } from "./routes";
import useIsFeatureEnabled, { Feature } from "./utils/useIsFeatureEnabled";
import {
  AngleDoubleLeftIcon,
  AngleDoubleRightIcon,
  OutlinedWindowRestoreIcon,
  CubesIcon,
  NetworkIcon,
  UserIcon,
  UsersIcon,
  OutlinedClockIcon,
  OutlinedCalendarAltIcon,
  CogIcon,
  LockIcon,
  DatabaseIcon,
  HeadsetIcon,
  InfrastructureIcon,
  PrintIcon,
  KeyIcon,
  TenantIcon,
  SecurityIcon,
} from "@patternfly/react-icons";

import "./page-nav.css";

type LeftNavProps = {
  title: string;
  path: string;
  id?: string;
  icon?: ReactNode;
  showTitle: boolean;
};

const LeftNav = ({ title, path, id, icon, showTitle }: LeftNavProps) => {
  const { t } = useTranslation();
  const { hasAccess } = useAccess();
  const { realm } = useRealm();
  const encodedRealm = encodeURIComponent(realm);
  const isExternalLink = path === "https://www.ztrust.in/contact";

  const route = routes.find(
    (route) =>
      route.path.replace(/\/:.+?(\?|(?:(?!\/).)*|$)/g, "") === (id || path),
  );

  const accessAllowed =
    route &&
    (route.handle.access instanceof Array
      ? hasAccess(...route.handle.access)
      : hasAccess(route.handle.access));

  if (!accessAllowed && !isExternalLink) {
    return undefined;
  }

  const name = "nav-item" + path.replace("/", "-");

  return (
    <li>
      {isExternalLink ? (
        <a
          id="nav-item-helpdesk"
          href="#"
          className="pf-v5-c-nav__link"
          onClick={(e) => {
            e.preventDefault();
            window.open(path, "_blank");
          }}
        >
          {icon && (
            <span
              className={`icon ${showTitle ? "icon-expanded" : "icon-collapsed"}`}
            >
              {icon}
            </span>
          )}
          {t(showTitle ? title : "")}
        </a>
      ) : (
        <NavLink
          id={name}
          to={`/${encodedRealm}${path}`}
          className={({ isActive }) =>
            `pf-v5-c-nav__link${isActive ? " pf-m-current" : ""}`
          }
        >
          {icon && (
            <span
              className={`icon ${showTitle ? "icon-expanded" : "icon-collapsed"}`}
            >
              {icon}
            </span>
          )}
          {t(showTitle ? title : "")}
        </NavLink>
      )}
    </li>
  );
};

const TooltipNav = ({ title, path, id, icon, showTitle }: LeftNavProps) => {
  const { t } = useTranslation();
  return !showTitle ? (
    <Tooltip
      content={<span style={{ color: "#ffffff" }}>{t(title)}</span>}
      animationDuration={800}
      position="right"
    >
      <LeftNav
        id={id}
        title={title}
        path={path}
        icon={icon}
        showTitle={showTitle}
      />
    </Tooltip>
  ) : (
    <LeftNav
      id={id}
      title={title}
      path={path}
      icon={icon}
      showTitle={showTitle}
    />
  );
};

export const PageNav = () => {
  const { t } = useTranslation();
  const { environment } = useEnvironment<Environment>();
  const { hasSomeAccess } = useAccess();
  const { componentTypes } = useServerInfo();
  const isFeatureEnabled = useIsFeatureEnabled();
  const pages =
    componentTypes?.["org.keycloak.services.ui.extend.UiPageProvider"];
  const navigate = useNavigate();
  const { realm, realmRepresentation } = useRealm();
  const [showSidebar, setShowSidebar] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1400) {
        setShowSidebar(false);
      } else {
        setShowSidebar(true);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  type SelectedItem = {
    groupId: number | string;
    itemId: number | string;
    to: string;
    event: FormEvent<HTMLInputElement>;
  };

  const onSelect = (item: SelectedItem) => {
    navigate(item.to);
    item.event.preventDefault();
  };

  const showManage = hasSomeAccess(
    "view-realm",
    "query-groups",
    "query-users",
    "query-clients",
    "view-events",
  );

  const showConfigure = hasSomeAccess(
    "view-realm",
    "query-clients",
    "view-identity-providers",
  );

  const showManageRealm = environment.masterRealm === environment.realm;

  return (
    <PageSidebar
      className={`keycloak__page_nav__nav ${showSidebar ? "expanded" : "collapsed"}`}
      isSidebarOpen={showSidebar}
    >
      <PageSidebarBody>
        <Nav onSelect={(_event, item) => onSelect(item as SelectedItem)}>
          <div className="nav-item-container">
            <div
              className={`nav-item-container ${showSidebar ? "visible" : "hidden"}`}
            >
              <h2
                className="pf-v5-c-nav__section-title"
                style={{
                  wordWrap: "break-word",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  gap: "6px",
                  marginLeft: "16px",
                  padding: 0,
                }}
              >
                <Label color="blue" style={{ width: "fit-content", fontSize: "14px" }}>
                  {t("currentRealm")}
                </Label>
                <span
                  style={{
                    color: "rgb(255, 255, 255)",
                    fontSize: "16px",
                    fontWeight: "500",
                    marginLeft: "7px",
                    width: "max-content",
                  }}
                  data-testid="currentRealm"
                >
                  {label(t, realmRepresentation?.displayName, realm)}
                </span>
              </h2>


            </div>
            <div className="button-container">
              <Button
                variant="link"
                onClick={() => setShowSidebar(!showSidebar)}
                aria-label={`sidebar-container`}
                className="sidebar-toggle"
              >
                {showSidebar ? (
                  <AngleDoubleLeftIcon />
                ) : (
                  <AngleDoubleRightIcon />
                )}
              </Button>
            </div>
          </div>

          {/* Manage Realm */}
          {showManageRealm && (
            <NavGroup>
              <TooltipNav
                title={t("manageRealms")}
                path="/realms"
                icon={<TenantIcon />}
                showTitle={showSidebar}
              />
            </NavGroup>
          )}

          {/* Manage Section */}
          {showManage && (
            <NavGroup
              aria-label={t("manage")}
              title={t(showSidebar ? "manage" : "")}
              style={{ color: "#ffffff", fontStyle: "800" }}
            >
              {isFeatureEnabled(Feature.Organizations) &&
                realmRepresentation?.organizationsEnabled && (
                  <TooltipNav
                    title="organizations"
                    path="/organizations"
                    icon={<InfrastructureIcon />}
                    showTitle={showSidebar}
                  />
                )}
              <TooltipNav
                title="clients"
                path="/clients"
                icon={<OutlinedWindowRestoreIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="clientScopes"
                path="/client-scopes"
                icon={<CubesIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="realmRoles"
                path="/roles"
                icon={<NetworkIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="users"
                path="/users"
                icon={<UserIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="groups"
                path="/groups"
                icon={<UsersIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="sessions"
                path="/sessions"
                icon={<OutlinedClockIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="events"
                path="/events"
                icon={<OutlinedCalendarAltIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="reports"
                path="/user-reports"
                icon={<PrintIcon />}
                showTitle={showSidebar}
              />
            </NavGroup>
          )}

          {/* Configure Section */}
          {showConfigure && (
            <NavGroup
              aria-label={t("configure")}
              title={t(showSidebar ? "configure" : "")}
              style={{ color: "#ffffff", fontStyle: "800" }}
            >
              <TooltipNav
                title="realmSettings"
                path="/realm-settings"
                icon={<CogIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="authentication"
                path="/authentication"
                icon={<LockIcon />}
                showTitle={showSidebar}
              />
              {/* {isFeatureEnabled(Feature.AdminFineGrainedAuthzV2) &&
                realmRepresentation?.adminPermissionsEnabled && (
                  <TooltipNav
                    title="permissions"
                    path="/permissions"
                    icon={<SecurityIcon />}
                    showTitle={showSidebar}
                  />
                )} */}
              <TooltipNav
                title="identityProviders"
                path="/identity-providers"
                icon={<KeyIcon />}
                showTitle={showSidebar}
              />
              <TooltipNav
                title="userFederation"
                path="/user-federation"
                icon={<DatabaseIcon />}
                showTitle={showSidebar}
              />
              {isFeatureEnabled(Feature.DeclarativeUI) &&
                pages?.map((p) => (
                  <TooltipNav
                    key={p.id}
                    title={p.id}
                    path={toPage({ providerId: p.id }).pathname!}
                    id="/page-section"
                    showTitle={showSidebar}
                  />
                ))}
            </NavGroup>
          )}

          {/* Support Section */}
          {(showManage || showConfigure) && (
            <NavGroup
              aria-label={t("support")}
              title={t(showSidebar ? "support" : "")}
              style={{ color: "#ffffff", fontStyle: "800" }}
            >
              <TooltipNav
                title="helpdesk"
                path="/help-desk"
                icon={<PrintIcon />}
                showTitle={showSidebar}
              />
            </NavGroup>
          )}
        </Nav>
      </PageSidebarBody>
    </PageSidebar >
  );
};
